import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { SEO } from '../components/seo/SEO';
import { client } from '../lib/sanity';
import { BlogHeader } from '../components/blog/BlogHeader';
import { BlogPostCard } from '../components/blog/BlogPostCard';
import { BlogPagination } from '../components/blog/BlogPagination';
import { BlogSidebar } from '../components/blog/BlogSidebar';
import type { Post } from '../types/sanity';
import { LoadingMessage } from '../components/common/LoadingMessage';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { getBreadcrumbSchema } from '../lib/jsonLd';

const POSTS_PER_PAGE = 12;

export function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPage = Number.isFinite(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const [posts, setPosts] = React.useState<Post[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [fetchAttempted, setFetchAttempted] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState<string | null>(null);

  // Extract unique categories from posts
  const categories = React.useMemo(() => {
    const allCategories = posts.flatMap(post => 
      post.categories?.map(category => category.title) || []
    );
    return [...new Set(allCategories)];
  }, [posts]);

  // Filter posts based on search query and category
  const filteredPosts = React.useMemo(() => {
    return posts.filter(post => {
      const matchesSearch =
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (post.excerpt || '').toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        !categoryFilter ||
        post.categories?.some(category => category.title === categoryFilter);

      return matchesSearch && matchesCategory;
    });
  }, [posts, searchQuery, categoryFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  // Clamp out-of-range page numbers (e.g. user lands on ?page=99 after a filter
  // shrinks the result set) — keep them on the last valid page instead of
  // showing an empty grid.
  const clampedPage = Math.min(currentPage, totalPages);
  const paginatedPosts = filteredPosts.slice(
    (clampedPage - 1) * POSTS_PER_PAGE,
    clampedPage * POSTS_PER_PAGE
  );

  // Reset to page 1 whenever the filter changes (search query or category).
  React.useEffect(() => {
    if (searchParams.get('page')) {
      const next = new URLSearchParams(searchParams);
      next.delete('page');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, categoryFilter]);

  const handlePageChange = (page: number) => {
    const next = new URLSearchParams(searchParams);
    if (page === 1) next.delete('page');
    else next.set('page', String(page));
    setSearchParams(next);
    // Bring the user back to the top of the list — otherwise pagination feels
    // broken when the new page renders far below the current scroll position.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  React.useEffect(() => {
    const fetchPosts = async () => {
      try {
        // Use a more specific query to avoid duplicates (exclude drafts)
        const result = await client.fetch<Post[]>(`*[_type == "post" && !(_id in path("drafts.**"))] | order(publishedAt desc) {
          _id,
          title,
          slug,
          mainImage,
          publishedAt,
          author->{
            name,
            image
          },
          excerpt,
          categories[]->{
            title
          }
        }`);
        
        // Filter out any posts without required fields
        const validPosts = result.filter(post => 
          post._id && 
          post.title && 
          post.slug && 
          post.slug.current
        );
        
        setPosts(validPosts);
        setLoading(false);
        setFetchAttempted(true);
      } catch (err) {
        console.error('Error fetching posts:', err);
        setError('Nie udało się załadować wpisów');
        setLoading(false);
        setFetchAttempted(true);
      }
    };

    fetchPosts();
  }, []);

  if (loading && !fetchAttempted) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <LoadingMessage ariaLabel="Ładowanie wpisów bloga" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <ErrorMessage message={error} ariaLabel="Błąd ładowania wpisów bloga" />
      </div>
    );
  }
  
  const hasNoPosts = posts.length === 0 && fetchAttempted && !error;

  const handleCategorySelect = (category: string) => {
    setCategoryFilter(category);
  };

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Strona główna', url: 'https://znajdki.pl/' },
    { name: 'Blog', url: 'https://znajdki.pl/aktualnosci' }
  ]);

  return (
    <div className="bg-accessible-gray-50 dark:bg-accessible-gray-900 high-contrast:bg-black py-16">
      <SEO 
        title="Blog"
        description="Aktualności, porady i historie adopcyjne z życia fundacji. Dowiedz się więcej o naszych działaniach i podopiecznych."
        url="/aktualnosci"
        image="/images/animals/fundacja-znajdki-zwierzeta-001.webp"
        jsonLd={breadcrumbSchema}
      />
      
      <div className="max-w-7xl mx-auto px-4">
        <BlogHeader 
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          categories={categories}
        />

        {loading && !fetchAttempted ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-12 h-12 border-4 border-accessible-red-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black rounded-lg shadow-lg">
            <p className="text-xl text-accessible-red-600">
              {error}
            </p>
          </div>
        ) : hasNoPosts ? (
          <div className="text-center py-12 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black rounded-lg shadow-lg">
            <h3 className="heading-4 mb-4">Brak wpisów na blogu</h3>
            <p className="body-medium">
              Aktualnie nie ma żadnych wpisów na blogu. Zapraszamy wkrótce!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3">
              {filteredPosts.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black rounded-lg shadow-lg">
                  <h3 className="heading-4 mb-4">Brak wyników wyszukiwania</h3>
                  <p className="body-medium">
                    Nie znaleziono wpisów pasujących do podanych kryteriów.
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {paginatedPosts.map((post) => (
                      <BlogPostCard key={post._id} post={post} />
                    ))}
                  </div>
                  <BlogPagination
                    currentPage={clampedPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </>
              )}
            </div>
            
            <div className="lg:col-span-1">
              <BlogSidebar 
                recentPosts={posts.slice(0, 3)} 
                categories={categories}
                onCategorySelect={handleCategorySelect}
              />
            </div>
          </div>
        )}
        
        {/* Newsletter signup for blog readers - HIDDEN */}
        {/* <div className="mt-16 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
          <div className="text-center">
            <h2 className="heading-3 mb-4">Zapisz się do newslettera</h2>
            <p className="body-medium max-w-2xl mx-auto mb-6">
              Otrzymuj najnowsze informacje o naszych działaniach, historiach adopcyjnych i potrzebujących zwierzakach.
            </p>
            <div className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  placeholder="Twój adres email"
                  className="flex-1 px-4 py-3 border border-accessible-gray-300 dark:border-accessible-gray-600 rounded-lg focus:ring-2 focus:ring-accessible-red-600 focus:border-transparent bg-white dark:bg-accessible-gray-700 text-accessible-gray-900 dark:text-white"
                />
                <button className="bg-accessible-red-600 text-white px-6 py-3 rounded-lg hover:bg-accessible-red-700 transition-colors font-medium">
                  Zapisz się
                </button>
              </div>
              <p className="text-xs text-accessible-gray-700 dark:text-white/90 high-contrast:text-white mt-2">
                Twoje dane są bezpieczne. W każdej chwili możesz zrezygnować z subskrypcji.
              </p>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
}